import React, { useState, useEffect, useRef } from 'react';
import { Search, X, Loader2 } from 'lucide-react';
import { addSearchHistory, getSearchHistory } from '../utils/storage';
import { Language, t } from '../utils/i18n';

interface SearchBoxProps {
  onSearch: (query: string) => void;
  isLoading?: boolean;
  hasResults?: boolean;
  onClear?: () => void;
  lang: Language;
  mode?: 'tool' | 'skill'; // 新增：搜索模式
}

const POPULAR_SEARCHES_ZH = ['Zabbix', 'MySQL', 'Docker', 'Git', 'Prometheus', 'Redis'];
const POPULAR_SEARCHES_EN = ['Zabbix', 'MySQL', 'Docker', 'Git', 'Prometheus', 'Redis'];

const SUGGESTIONS_ZH = [
  { name: 'Zabbix', desc: '企业级监控工具' },
  { name: 'MySQL', desc: '关系型数据库' },
  { name: 'Docker', desc: '容器化平台' },
  { name: 'Git', desc: '版本控制系统' },
  { name: 'Prometheus', desc: '云原生监控' },
  { name: 'Redis', desc: '键值存储' },
  { name: 'Nginx', desc: 'Web服务器' },
  { name: 'PostgreSQL', desc: '关系型数据库' },
  { name: 'Kubernetes', desc: '容器编排' },
  { name: 'Jenkins', desc: 'CI/CD工具' }
];

const SUGGESTIONS_EN = [
  { name: 'Zabbix', desc: 'Enterprise Monitoring Tool' },
  { name: 'MySQL', desc: 'Relational Database' },
  { name: 'Docker', desc: 'Container Platform' },
  { name: 'Git', desc: 'Version Control System' },
  { name: 'Prometheus', desc: 'Cloud-Native Monitoring' },
  { name: 'Redis', desc: 'Key-Value Store' },
  { name: 'Nginx', desc: 'Web Server' },
  { name: 'PostgreSQL', desc: 'Relational Database' },
  { name: 'Kubernetes', desc: 'Container Orchestration' },
  { name: 'Jenkins', desc: 'CI/CD Tool' }
];

// SKILL 搜索建议（新增）
const SKILL_SUGGESTIONS_ZH = [
  { name: 'filesystem', desc: '文件系统操作' },
  { name: 'github', desc: 'GitHub 仓库集成' },
  { name: 'database', desc: '数据库操作' },
  { name: 'slack', desc: 'Slack 消息发送' },
  { name: 'search', desc: '网页搜索' },
  { name: 'fetch', desc: 'HTTP 请求' }
];

const SKILL_SUGGESTIONS_EN = [
  { name: 'filesystem', desc: 'File system operations' },
  { name: 'github', desc: 'GitHub repository integration' },
  { name: 'database', desc: 'Database operations' },
  { name: 'slack', desc: 'Slack messaging' },
  { name: 'search', desc: 'Web search' },
  { name: 'fetch', desc: 'HTTP requests' }
];

export default function SearchBox({ onSearch, isLoading, hasResults, onClear, lang, mode = 'tool' }: SearchBoxProps) {
  const [query, setQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState(
    lang === 'zh' ? SUGGESTIONS_ZH : SUGGESTIONS_EN
  );
  const [smartSuggestions, setSmartSuggestions] = useState<string[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const POPULAR_SEARCHES = lang === 'zh' ? POPULAR_SEARCHES_ZH : POPULAR_SEARCHES_EN;
  const SUGGESTIONS = lang === 'zh' ? SUGGESTIONS_ZH : SUGGESTIONS_EN;

  // 固定的示范建议
  const FIXED_SUGGESTIONS = lang === 'zh'
    ? ['我想写日记', '替代Photoshop的软件', 'Mac上的视频剪辑']
    : ['journaling app', 'Photoshop alternative', 'video editing on Mac'];

  const hasWarmedFixedRef = useRef(false);
  const prevHasResultsRef = useRef(hasResults);

  // 当语言改变时更新建议
  useEffect(() => {
    setFilteredSuggestions(lang === 'zh' ? SUGGESTIONS_ZH : SUGGESTIONS_EN);
    setSmartSuggestions([]); // 清空智能建议
    hasWarmedFixedRef.current = false; // 重置预热标记
  }, [lang]);

  // 当搜索结果从有变无时（点击返回首页），清空输入框
  useEffect(() => {
    if (prevHasResultsRef.current && !hasResults) {
      setQuery('');
      setShowSuggestions(false);
    }
    prevHasResultsRef.current = hasResults;
  }, [hasResults]);

  // 预热固定的示范建议和联想建议
  useEffect(() => {
    if (hasWarmedFixedRef.current) return;

    const warmupAllSuggestions = async () => {
      try {
        // 合并固定建议和联想建议
        const suggestions = lang === 'zh' ? SUGGESTIONS_ZH : SUGGESTIONS_EN;
        const allSuggestions = [...FIXED_SUGGESTIONS, ...suggestions.map(s => s.name)];

        console.log('🔥 预热所有建议（固定+联想）:', allSuggestions.length, '个');

        const response = await fetch('/api/warmup', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ queries: allSuggestions })
        });

        if (response.ok) {
          const data = await response.json();
          console.log('✅ 建议预热任务已启动:', data);
          hasWarmedFixedRef.current = true;
        }
      } catch (error) {
        console.log('⚠️ 建议预热失败:', error);
      }
    };

    // 延迟2秒执行，避免阻塞页面加载
    const timer = setTimeout(() => {
      warmupAllSuggestions();
    }, 2000);

    return () => clearTimeout(timer);
  }, [lang]);

  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionTimerRef = useRef<NodeJS.Timeout | null>(null);

  // 获取智能搜索建议
  const fetchSmartSuggestions = async (projectNames: string[]) => {
    if (loadingSuggestions || projectNames.length === 0) return;

    try {
      setLoadingSuggestions(true);
      console.log('🤖 生成智能搜索建议...');

      const response = await fetch('/api/smart-suggestions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectNames, lang })
      });

      const data = await response.json();

      if (data.suggestions && data.suggestions.length > 0) {
        setSmartSuggestions(data.suggestions);
        console.log('✅ 智能建议生成成功:', data.suggestions.length, '个');

        // 预热智能建议（异步执行）
        setTimeout(() => {
          fetch('/api/warmup', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ queries: data.suggestions })
          }).then(res => {
            if (res.ok) {
              console.log('🔥 智能建议预热任务已启动');
            }
          }).catch(err => {
            console.log('⚠️ 智能建议预热失败:', err);
          });
        }, 500);
      }
    } catch (error) {
      console.log('⚠️ 获取智能建议失败:', error);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  useEffect(() => {
    if (query.trim()) {
      const filtered = (lang === 'zh' ? SUGGESTIONS_ZH : SUGGESTIONS_EN).filter(
        item =>
          item.name.toLowerCase().includes(query.toLowerCase()) ||
          item.desc.includes(query)
      );
      setFilteredSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);

      // 5秒后自动隐藏建议框
      if (suggestionTimerRef.current) {
        clearTimeout(suggestionTimerRef.current);
      }
      suggestionTimerRef.current = setTimeout(() => {
        setShowSuggestions(false);
      }, 5000);
    } else {
      setFilteredSuggestions(lang === 'zh' ? SUGGESTIONS_ZH : SUGGESTIONS_EN);
      setShowSuggestions(false);
      if (suggestionTimerRef.current) {
        clearTimeout(suggestionTimerRef.current);
        suggestionTimerRef.current = null;
      }
    }

    // 清理函数：组件卸载时清除定时器
    return () => {
      if (suggestionTimerRef.current) {
        clearTimeout(suggestionTimerRef.current);
      }
    };
  }, [query, lang]);

  const handleSearch = () => {
    if (query.trim()) {
      addSearchHistory(query);
      onSearch(query.trim());
      setShowSuggestions(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleSuggestionClick = (name: string) => {
    setQuery(name);
    addSearchHistory(name);
    onSearch(name);
    setShowSuggestions(false);
  };

  const handleClear = () => {
    setQuery('');
    setShowSuggestions(false);
    if (onClear) {
      onClear();
    }
    inputRef.current?.focus();
  };

  return (
    <div className="relative max-w-3xl mx-auto">
      {/* 提示语 - 根据模式显示不同内容 */}
      {!hasResults && !query && (
        <div className={`mb-4 rounded-lg p-4 border ${
          mode === 'tool'
            ? 'bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-blue-100 dark:border-blue-800'
            : 'bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border-purple-200 dark:border-purple-700'
        }`}>
          <div className="text-sm text-gray-700 dark:text-gray-300 mb-2 font-medium">
            {mode === 'tool' ? (
              <>💡 {lang === 'zh' ? '自然语言搜索，懂你就好' : 'Natural Language Search'}</>
            ) : (
              <>✨ {lang === 'zh' ? 'MCP Skills 搜索' : 'Search MCP Skills'}</>
            )}
          </div>
          <div className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
            {mode === 'tool' ? (
              // 工具搜索提示
              lang === 'zh' ? (
                <>
                  <span className="text-gray-500 dark:text-gray-400">不需要知道工具名、分类或关键词</span>
                  <span className="mx-2">•</span>
                  <span className="text-gray-700 dark:text-gray-300">试试说：</span>
                  {FIXED_SUGGESTIONS.map((suggestion, index) => (
                    <button
                      key={`fixed-${index}`}
                      onClick={() => handleSuggestionClick(suggestion)}
                      className="mx-1 px-2 py-1 bg-white dark:bg-gray-800 rounded text-[#165DFF] hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors cursor-pointer"
                    >
                      "{suggestion}"
                    </button>
                  ))}
                  {/* 智能建议 */}
                  {smartSuggestions.length > 0 && (
                    <>
                      {loadingSuggestions && (
                        <span className="ml-2 text-gray-400">🤖 生成中...</span>
                      )}
                      {!loadingSuggestions && smartSuggestions && smartSuggestions.length > 0 && smartSuggestions.map((suggestion, index) => (
                        <button
                          key={`smart-${index}`}
                          onClick={() => handleSuggestionClick(suggestion)}
                          className="mx-1 px-2 py-1 bg-white dark:bg-gray-800 rounded text-[#165DFF] hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors cursor-pointer"
                          title="基于 GitHub Trending 智能生成"
                        >
                          "{suggestion}"
                        </button>
                      ))}
                    </>
                  )}
                </>
              ) : (
                <>
                  <span className="text-gray-500 dark:text-gray-400">No need to know tool names, categories, or keywords</span>
                  <span className="mx-2">•</span>
                  <span className="text-gray-700 dark:text-gray-300">Try:</span>
                  {FIXED_SUGGESTIONS.map((suggestion, index) => (
                    <button
                      key={`fixed-${index}`}
                      onClick={() => handleSuggestionClick(suggestion)}
                      className="mx-1 px-2 py-1 bg-white dark:bg-gray-800 rounded text-[#165DFF] hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors cursor-pointer"
                    >
                      "{suggestion}"
                    </button>
                  ))}
                  {/* 智能建议 */}
                  {smartSuggestions.length > 0 && (
                    <>
                      {loadingSuggestions && (
                        <span className="ml-2 text-gray-400">🤖 Generating...</span>
                    )}
                    {!loadingSuggestions && smartSuggestions.map((suggestion, index) => (
                      <button
                        key={`smart-${index}`}
                        onClick={() => handleSuggestionClick(suggestion)}
                        className="mx-1 px-2 py-1 bg-white dark:bg-gray-800 rounded text-[#165DFF] hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors cursor-pointer"
                        title="AI-generated from GitHub Trending"
                      >
                        "{suggestion}"
                      </button>
                    ))}
                  </>
                )}
              </>
            )
          ) : (
            // SKILL 搜索提示
            lang === 'zh' ? (
              <>
                <span className="text-gray-500 dark:text-gray-400">搜索 MCP Skills，为 AI 助手添加能力</span>
                <span className="mx-2">•</span>
                <span className="text-gray-700 dark:text-gray-300">试试：</span>
                {SKILL_SUGGESTIONS_ZH.map((suggestion, index) => (
                  <button
                    key={`skill-${index}`}
                    onClick={() => handleSuggestionClick(suggestion.name)}
                    className="mx-1 px-2 py-1 bg-white dark:bg-gray-800 rounded text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/30 transition-colors cursor-pointer"
                  >
                    "{suggestion.name}"
                  </button>
                ))}
              </>
            ) : (
              <>
                <span className="text-gray-500 dark:text-gray-400">Search MCP Skills to empower your AI assistant</span>
                <span className="mx-2">•</span>
                <span className="text-gray-700 dark:text-gray-300">Try:</span>
                {SKILL_SUGGESTIONS_EN.map((suggestion, index) => (
                  <button
                    key={`skill-${index}`}
                    onClick={() => handleSuggestionClick(suggestion.name)}
                    className="mx-1 px-2 py-1 bg-white dark:bg-gray-800 rounded text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/30 transition-colors cursor-pointer"
                  >
                    "{suggestion.name}"
                  </button>
                ))}
              </>
            )
          )}
          </div>
        </div>
      )}

      {/* 搜索框 */}
      <div className="relative">
        <div
          className={`
            relative bg-white dark:bg-[#2D2D3F] rounded-xl shadow-lg
            transition-all duration-300
            ${showSuggestions || query ? 'ring-2 ring-[#165DFF]' : ''}
          `}
        >
          <div className="flex items-center px-6 py-5">
            <Search className="w-6 h-6 text-gray-400 flex-shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyPress={handleKeyPress}
              onFocus={() => !query && setShowSuggestions(false)}
              placeholder={t('searchPlaceholder', lang)}
              className="flex-1 mx-4 bg-transparent outline-none text-gray-800 dark:text-gray-100 placeholder-gray-400 text-lg"
            />
            {query && (
              <button
                onClick={handleClear}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                aria-label={t('clear', lang)}
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            )}
            {isLoading ? (
              <Loader2 className="w-6 h-6 text-[#165DFF] animate-spin" />
            ) : (
              <button
                onClick={handleSearch}
                disabled={!query.trim()}
                className="px-6 py-2 bg-[#165DFF] text-white rounded-lg hover:bg-[#0E4FD0] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {t('searchButton', lang)}
              </button>
            )}
          </div>

          {/* 加载进度条 */}
          {isLoading && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#165DFF] animate-pulse" />
          )}
        </div>

        {/* 联想建议下拉框 */}
        {showSuggestions && filteredSuggestions.length > 0 && (
          <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-[#2D2D3F] rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden z-50">
            {filteredSuggestions.map((item, index) => (
              <button
                key={index}
                onClick={() => handleSuggestionClick(item.name)}
                className="w-full px-6 py-3 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-left"
              >
                <div>
                  <div className="text-gray-800 dark:text-gray-100">{item.name}</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">{item.desc}</div>
                </div>
                <Search className="w-4 h-4 text-gray-400" />
              </button>
            ))}
          </div>
        )}
      </div>

    </div>
  );
}
